import { t, useLanguage } from "@/i18n/LanguageContext";import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import ListEditor from "@/pages/admin/ListEditor";
import AiButton from "@/pages/admin/AiButton";

function Field({ label, value, onChange, textarea, testid, hint, aiField, onAuthError }) {useLanguage();
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t(label)}</label>
        {t(aiField &&
        <AiButton field={aiField} getText={() => value} onResult={onChange} onAuthError={onAuthError} testid={`${testid}-ai-button`} />)
        }
      </div>
      {t(textarea ?
      <textarea rows={textarea} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" /> :

      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />)
      }
      {t(hint && <p className="mt-1 text-[10px] text-slate-400">{t(hint)}</p>)}
    </div>);

}

export default function ContentTab({ onAuthError }) {useLanguage();
  const { content, contentLoaded, contentError, refreshContent } = useSite();
  const [draft, setDraft] = useState(() => structuredClone(content));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setDraft(structuredClone(content));
  }, [content, dirty]);

  const setPath = (path, value) => {
    setDirty(true);
    setDraft((d) => {
      const c = structuredClone(d);
      const keys = path.split(".");
      let o = c;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return c;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      await refreshContent();
      setDirty(false);
      toast.success(t("Contenuti salvati e pubblicati sul sito"));
    } catch (e) {
      if (e.response?.status === 401) onAuthError();else
      toast.error(t("Salvataggio non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  if (!contentLoaded) {
    return <div data-testid="content-loading" role="status" className="rounded-2xl bg-white p-6 text-sm text-slate-600">
      {t(contentError || "Caricamento dei contenuti aggiornati…")}
      {t(contentError && <button data-testid="content-retry-button" onClick={refreshContent} className="ml-3 font-bold text-brand-navy underline">{t("Riprova")}</button>)}
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="content-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand-lime/50 bg-brand-limelight px-6 py-4">
        <p className="text-sm text-brand-navydeep">
          <strong>{t("Modifica e salva:")}</strong>{t(" le modifiche sono subito visibili sul sito pubblico.")}
        </p>
        <div className="flex gap-2">
          <button data-testid="content-reset-button" onClick={() => {setDirty(false);setDraft(structuredClone(content));}}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/20 px-4 py-2 text-xs font-bold text-brand-navy hover:bg-white">
            <RotateCcw className="h-3.5 w-3.5" />{t(" Annulla modifiche")}
          </button>
          <button data-testid="content-save-button" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy px-5 py-2 text-xs font-bold text-white hover:bg-brand-navydark disabled:opacity-60">
            <Save className="h-3.5 w-3.5" /> {t(saving ? "Salvataggio…" : "Salva tutto")}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-brand-navydeep">{t("Hero (Home)")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Occhiello")} value={draft.hero.eyebrow} onChange={(v) => setPath("hero.eyebrow", v)} testid="content-hero-eyebrow" />
          <Field label={t("Badge cantieri")} value={draft.hero.badge} onChange={(v) => setPath("hero.badge", v)} testid="content-hero-badge" />
          <Field label={t("Titolo — riga 1")} value={draft.hero.line1} onChange={(v) => setPath("hero.line1", v)} testid="content-hero-line1" />
          <Field label={t("Titolo — riga 2 (lime)")} value={draft.hero.line2} onChange={(v) => setPath("hero.line2", v)} testid="content-hero-line2" />
          <Field label={t("Titolo — riga 3")} value={draft.hero.line3} onChange={(v) => setPath("hero.line3", v)} testid="content-hero-line3" />
          <Field label={t("Immagine hero (URL)")} value={draft.hero.image} onChange={(v) => setPath("hero.image", v)} testid="content-hero-image" />
          <div className="sm:col-span-2">
            <Field label={t("Video hero (URL .mp4, opzionale)")} value={draft.hero.video || ""} onChange={(v) => setPath("hero.video", v)}
            testid="content-hero-video" hint="Se compilato, il video sostituisce l'immagine di sfondo. Lascia vuoto per tornare all'immagine." />
          </div>
          <div className="sm:col-span-2">
            <Field label={t("Sottotitolo")} textarea={3} value={draft.hero.subtitle} onChange={(v) => setPath("hero.subtitle", v)}
            testid="content-hero-subtitle" aiField="Sottotitolo della hero in Home" onAuthError={onAuthError} />
          </div>
        </div>
      </div>

      <ListEditor title={t("Numeri aziendali")} items={draft.stats} testid="stats"
      fields={[{ key: "value", label: "Valore (es. 14, 50+)" }, { key: "label", label: "Etichetta" }]}
      onChange={(v) => setPath("stats", v)} />

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-brand-navydeep">{t("Chi siamo")}</h3>
        <div className="space-y-4">
          <Field label={t("Titolo")} value={draft.about.title} onChange={(v) => setPath("about.title", v)} testid="content-about-title" />
          <Field label={t("Testo (riga vuota tra i paragrafi)")} textarea={7} value={draft.about.text} onChange={(v) => setPath("about.text", v)}
          testid="content-about-text" aiField="Testo principale della pagina Chi siamo" onAuthError={onAuthError} />
          <Field label={t("Immagine (URL)")} value={draft.about.image} onChange={(v) => setPath("about.image", v)} testid="content-about-image" />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-brand-navydeep">{t("Macroarea BIM — esempi di riferimento")}</h3>
        <div className="space-y-4">
          <Field label={t("Titolo")} value={draft.bim?.title} onChange={(v) => setPath("bim.title", v)} testid="content-bim-title" />
          <Field label={t("Introduzione")} textarea={3} value={draft.bim?.description} onChange={(v) => setPath("bim.description", v)} testid="content-bim-description" />
          <Field label={t("Nota immagini di terzi e licenze")} textarea={3} value={draft.bim?.disclaimer} onChange={(v) => setPath("bim.disclaimer", v)} testid="content-bim-disclaimer" />
        </div>
      </div>
      <ListEditor title={t("Modelli BIM esplorabili")} items={draft.bim?.projects || []} testid="bim-projects"
      fields={[
      { key: "title", label: "Titolo" }, { key: "category", label: "Ambito" },
      { key: "description", label: "Descrizione", textarea: true, wide: true },
      { key: "image", label: "URL immagine senza obbligo di attribuzione" }]
      }
      onChange={(v) => setPath("bim.projects", v.map((item) => ({ ...item, id: item.id || crypto.randomUUID() })))} />

      <ListEditor title={t("Organigramma societario")} items={draft.organigram || []} testid="organigram"
      fields={[
      { key: "title", label: "Area / Ruolo" },
      { key: "icon", label: "Icona (nome Lucide)" },
      { key: "image", label: "Immagine (percorso, es. /organigramma/direzione.png)" },
      { key: "detail", label: "Composizione (persone e ruoli)", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("organigram", v)} />

      <ListEditor title={t("Macroaree e slogan")} items={draft.macroareas || []} testid="macroareas"
      fields={[{ key: "badge", label: "Etichetta" }, { key: "title", label: "Titolo" }, { key: "text", label: "Testo", textarea: true, wide: true }]}
      onChange={(v) => setPath("macroareas", v)} />
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-display font-bold text-brand-navydeep">{t("Sport, sociale e nuove richieste")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Sport e sociale — titolo")} value={draft.social?.title} onChange={(v) => setPath("social.title", v)} testid="content-social-title" />
          <Field label={t("Sport e sociale — testo")} value={draft.social?.text} onChange={(v) => setPath("social.text", v)} testid="content-social-text" textarea={4} />
          <Field label={t("Lavora con noi — titolo")} value={draft.forms?.career?.title} onChange={(v) => setPath("forms.career.title", v)} testid="content-career-title" />
          <Field label={t("Lavora con noi — testo")} value={draft.forms?.career?.description} onChange={(v) => setPath("forms.career.description", v)} testid="content-career-description" textarea={4} />
          <Field label={t("Preventivo — titolo")} value={draft.forms?.quote?.title} onChange={(v) => setPath("forms.quote.title", v)} testid="content-quote-title" />
          <Field label={t("Preventivo — testo")} value={draft.forms?.quote?.description} onChange={(v) => setPath("forms.quote.description", v)} testid="content-quote-description" textarea={4} />
        </div>
      </div>
      <ListEditor title={t("Squadre sponsorizzate")} items={draft.social?.teams || []} testid="sponsors"
      fields={[{ key: "name", label: "Squadra" }, { key: "desc", label: "Descrizione" }, { key: "logo", label: "URL stemma" }]}
      onChange={(v) => setPath("social.teams", v)} />

      <ListEditor title={t("Sedi operative")} items={draft.sedi || []} testid="sedi"
      fields={[
      { key: "title", label: "Nome sede" },
      { key: "ownership", label: "Proprietà" },
      { key: "desc", label: "Descrizione", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("sedi", v)} />

      <ListEditor title={t("Partner (banda a piè di pagina)")} items={draft.partners || []} testid="partners"
      fields={[
      { key: "name", label: "Nome" },
      { key: "initials", label: "Iniziali logo (2-3 lettere)" },
      { key: "kind", label: "Tipologia (es. Comune, Azienda partner)" },
      { key: "logo", label: "Logo (percorso, es. /partners/unicas.jpg — opzionale)" }]
      }
      onChange={(v) => setPath("partners", v)} />

      <ListEditor title={t("Servizi")} items={draft.services} testid="services"
      fields={[
      { key: "title", label: "Titolo" },
      { key: "icon", label: "Icona (nome Lucide, es. Landmark)" },
      { key: "desc", label: "Descrizione", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("services", v)} />

      <ListEditor title={t("Uffici")} items={draft.offices} testid="offices"
      fields={[
      { key: "title", label: "Nome ufficio" },
      { key: "icon", label: "Icona (nome Lucide)" },
      { key: "desc", label: "Descrizione", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("offices", v)} />

      <ListEditor title={t("Certificazioni")} items={draft.certifications} testid="certifications"
      fields={[
      { key: "code", label: "Codice (es. ISO 9001)" },
      { key: "title", label: "Titolo" },
      { key: "number", label: "N. certificato (opzionale)" },
      { key: "expiry", label: "Scadenza (opzionale)" },
      { key: "org", label: "Ente rilascio (opzionale)" },
      { key: "file", label: "File PDF (es. /certificazioni/iso-9001.pdf)" },
      { key: "desc", label: "Descrizione", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("certifications", v)} />

      <ListEditor title={t("Galleria immagini (Home)")} items={draft.gallery || []} testid="gallery"
      fields={[
      { key: "image", label: "Immagine (URL)" },
      { key: "label", label: "Didascalia" }]
      }
      onChange={(v) => setPath("gallery", v)} />

      <ListEditor title={t("Parco mezzi")} items={draft.fleet} testid="fleet"
      fields={[
      { key: "title", label: "Mezzo" },
      { key: "icon", label: "Icona (nome Lucide, es. Tractor)" },
      { key: "desc", label: "Descrizione", textarea: true, wide: true }]
      }
      onChange={(v) => setPath("fleet", v)} />

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-brand-navydeep">{t("Contatti (visibili nel sito)")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Indirizzo")} value={draft.contact.address} onChange={(v) => setPath("contact.address", v)} testid="content-contact-address" />
          <Field label={t("Telefono")} value={draft.contact.phone} onChange={(v) => setPath("contact.phone", v)} testid="content-contact-phone" />
          <Field label={t("Email")} value={draft.contact.email} onChange={(v) => setPath("contact.email", v)} testid="content-contact-email" />
          <Field label={t("PEC")} value={draft.contact.pec} onChange={(v) => setPath("contact.pec", v)} testid="content-contact-pec" />
          <Field label={t("Codice fiscale / P.IVA")} value={draft.contact.piva} onChange={(v) => setPath("contact.piva", v)} testid="content-contact-piva" />
          <Field label={t("Capitale sociale")} value={draft.contact.capital} onChange={(v) => setPath("contact.capital", v)} testid="content-contact-capital" />
          <Field label={t("Impianto — indirizzo")} value={draft.contact.plant} onChange={(v) => setPath("contact.plant", v)} testid="content-contact-plant" />
          <Field label={t("Impianto — proprietà")} value={draft.contact.plantOwnership} onChange={(v) => setPath("contact.plantOwnership", v)} testid="content-contact-plant-ownership" />
          <Field label={t("Orari")} value={draft.contact.hours} onChange={(v) => setPath("contact.hours", v)} testid="content-contact-hours" />
        </div>
      </div>
    </div>);

}
